import React from 'react';
import { Box, Typography, Chip, Button } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { IContest, IProject } from '../../types/global/helper';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useProgressiveImage } from '../../hook/useProgressiveLoad';
import { getCurrentContestPhaseEndData } from '../EsContestSubmission/helper';
import CountdownTimer from '../CountdownTimer/CountdownTimer';
import { EsBackButton } from '../EsButtons/EsBackButton';
import { calcTotalPool, contestHasAdditionalPrizes, formatContestPool, shouldShowPoolSuffix, withAlpha } from './utils';

interface Props {
    contest: IContest;
    onBackButtonPressed?: () => void;
    onPlayVideo?: (video: IProject) => void;
}

const phaseLabel = (phase?: string) => {
    switch (phase) {
        case 'submission':
            return 'Active';
        case 'vote':
            return 'Voting';
        case 'closed':
            return 'Closed';
        default:
            return 'Preparation';
    }
};

const titleEndsWithContest = (title: string) => /contest$/i.test(title.trim());

export const ContestDetailHero: React.FC<Props> = ({ contest, onBackButtonPressed, onPlayVideo }) => {
    const { themeStyle, device } = useThemeContext();
    const t = theme[themeStyle];

    const selectedImage =
        device.isMobile && contest.mobile_feature_image
            ? contest.mobile_feature_image
            : contest.contest_video?.thumbnail || contest.feature_image;
    const { sourceLoaded } = useProgressiveImage(selectedImage);
    const expirationDate = getCurrentContestPhaseEndData(contest)?.expiration_date;
    const isActive = contest.contest_phase === 'submission';
    const totalPool = calcTotalPool(contest);
    const showReward = totalPool > 0 || contestHasAdditionalPrizes(contest);
    const hasVideo = !!contest.contest_video?.hls;

    return (
        <Box
            sx={{
                position: 'relative',
                height: { xs: 400, md: 440 },
                width: '100%',
                borderRadius: 0,
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
                overflow: 'hidden',
            }}>
            {sourceLoaded ? (
                <Box
                    component="img"
                    src={sourceLoaded}
                    alt={contest.title}
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        filter: 'brightness(0.75)',
                        transition: 'transform 0.7s',
                        '&:hover': { transform: 'scale(1.05)' },
                    }}
                />
            ) : (
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        background: `linear-gradient(135deg, ${withAlpha(t.highlight, 0.19)} 0%, ${t.pure_black} 100%)`,
                    }}
                />
            )}

            {/* Gradient */}
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    background: `linear-gradient(180deg, transparent 0%, ${t.pure_black} 100%)`,
                }}
            />

            {/* Top bar: back button + play */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    p: { xs: 2, md: 3 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}>
                <EsBackButton handleDetails={onBackButtonPressed} />
                {hasVideo && (
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => onPlayVideo?.(contest.contest_video as IProject)}
                        sx={{
                            backgroundColor: 'white',
                            color: 'black',
                            '&:hover': { backgroundColor: '#e5e7eb' },
                            fontSize: { xs: 10, sm: 12 },
                        }}>
                        Watch Campaign Video
                    </Button>
                )}
            </Box>

            {/* Content overlay */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: { xs: 3, md: 4 },
                }}>
                {/* Badges */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Chip
                        label={phaseLabel(contest.contest_phase)}
                        size="small"
                        sx={{
                            bgcolor: isActive ? withAlpha(t.btnCustomColor, 0.13) : withAlpha(t.highlight, 0.13),
                            border: `1px solid ${
                                isActive ? withAlpha(t.btnCustomColor, 0.25) : withAlpha(t.highlight, 0.19)
                            }`,
                            color: isActive ? t.btnCustomColor : t.highlight,
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: '0.15em',
                            textTransform: 'uppercase',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        }}
                    />
                    {contest.premium && (
                        <Chip
                            label="Limited Entry"
                            size="small"
                            sx={{
                                bgcolor: withAlpha(t.highlight, 0.08),
                                border: `1px solid ${withAlpha(t.highlight, 0.15)}`,
                                color: t.highlight,
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: '0.15em',
                                textTransform: 'uppercase',
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            }}
                        />
                    )}
                </Box>

                {/* Title with "Contest" suffix */}
                <Typography
                    sx={{
                        fontSize: { xs: 32, md: 52 },
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        color: t.true_white,
                        letterSpacing: '-0.04em',
                        lineHeight: 1,
                        mb: 3,
                    }}>
                    {contest.title}
                    {!titleEndsWithContest(contest.title) && (
                        <Box component="span" sx={{ color: t.highlight }}>
                            {' '}
                            Contest
                        </Box>
                    )}
                </Typography>

                {/* Meta row + optional vote CTA */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'stretch', md: 'flex-end' },
                        justifyContent: 'space-between',
                        gap: { xs: 2.5, md: 3 },
                    }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 3, md: 5 }, alignItems: 'flex-end' }}>
                        {/* Total Pool / Gifted Prizes */}
                        {showReward && (
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.2em',
                                        fontWeight: 700,
                                        color: t.true_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    {shouldShowPoolSuffix(contest) ? 'Total Pool' : 'Starting Reward'}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PaymentsOutlinedIcon sx={{ color: t.highlight, fontSize: 20 }} />
                                    <Typography
                                        sx={{
                                            fontSize: 22,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: t.true_white,
                                        }}>
                                        {formatContestPool(contest)}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Countdown */}
                        {expirationDate && (
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.2em',
                                        fontWeight: 700,
                                        color: t.true_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        mb: 1,
                                    }}>
                                    Closing In
                                </Typography>
                                <CountdownTimer
                                    targetDate={expirationDate}
                                    fontSize={device.isMobile ? 10 : 12}
                                    size={device.isMobile ? 48 : 52}
                                    accentColor={t.highlight}
                                />
                            </Box>
                        )}

                        {/* Type */}
                        {contest.kind && (
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.2em',
                                        fontWeight: 700,
                                        color: t.true_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    Type
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 20,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: t.true_white,
                                    }}>
                                    {contest.kind === 'physical' ? 'In Person' : 'Online'}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
